/* eslint-disable jsx-a11y/anchor-is-valid */
import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import FpLoader from 'forepaas/core-ui/loader';
import Toaster from 'src/components/CustomComponent/Toaster';
import FpSdk from 'forepaas/sdk';
import MemoryActions from 'forepaas/store/memory/action';
import FpTranslate from 'forepaas/translate';
import authService from '../../../services/auth-service';
import FpPasswordForgot from '../FpPasswordForgot/FpPasswordForgot.jsx';
import FpNewPasswordRequired from '../FpNewPasswordRequired/FpNewPasswordRequired.jsx';
import '../styles/authentication.less';
import { MaintenanceApi } from '../../../openapi/apis/MaintenanceApi';
import { createAuthenticatedApiClient } from '../../../helpers/api-client';
import { format } from 'date-fns';
import { ja as jaLocale } from 'date-fns/locale';
import { signOut } from '../../../helpers/auth';
import { validateAuthMethod, showAuthMethodError } from '../../../helpers/auth-method-validator';


@connect((state) => ({
  user: state.memory.user
}))
class FpClientAuthorityManager extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      form: {
        login: '',
        password: '',
      },
      forgotPassword: false,
      challengeUser: null,
      // Add new isLoading
      isLoading: false,
      error: null,
      return:
        (props.preferences?.auth_mode || []).filter(
          auth =>
            (typeof auth.visible === 'undefined' || auth.visible) &&
            !auth.hidden
        ).length > 1,
      color: this.props.preferences?.color || '#00CCF9',
      login: {},
      // Add maintenance state
      maintenanceData: null,
      isLoadingMaintenance: false,
    };
    this.handleInputChange = this.handleInputChange.bind(this);
    this.handleIdpError = this.handleIdpError.bind(this);
  }

  componentDidMount() {
    // Fetch maintenance data when component mounts
    this.fetchMaintenanceData();
  }

  fetchMaintenanceData = async () => {
    this.setState({ isLoadingMaintenance: true });
    try {
      // Try with authenticated client first
      const maintenanceApi = createAuthenticatedApiClient(MaintenanceApi);
      const maintenanceData = await maintenanceApi.getScheduledMaintenance();
      this.setState({
        maintenanceData,
        isLoadingMaintenance: false
      });
    } catch (error) {
      // If authentication fails, try without authentication (public endpoint)
      try {
        const { createConfiguration } = await import('../../../helpers/api-client');
        const maintenanceApi = new MaintenanceApi(createConfiguration());
        const maintenanceData = await maintenanceApi.getScheduledMaintenance();
        this.setState({
          maintenanceData,
          isLoadingMaintenance: false
        });
      } catch (fallbackError) {
        console.error('Failed to fetch maintenance data:', fallbackError);
        this.setState({
          maintenanceData: null,
          isLoadingMaintenance: false
        });
      }
    }
  };

  formatMaintenanceDate = (dateString) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return format(date, 'yyyy年MM月dd日（EEE）HH:mm', { locale: jaLocale });
    } catch (error) {
      console.error('Failed to format date:', error);
      return dateString;
    }
  };

  renderMaintenanceNotice = () => {
    const { maintenanceData, isLoadingMaintenance } = this.state;

    if (isLoadingMaintenance) {
      return null;
    }

    // Don't show maintenance notice if no scheduled maintenance (id = 0)
    if (!maintenanceData || maintenanceData.id === 0) {
      return null;
    }

    const startTime = this.formatMaintenanceDate(maintenanceData.startTime);
    const endTime = this.formatMaintenanceDate(maintenanceData.endTime);

    return (
      <div className="maintenance-notice">
        <div className="notice-title">メンテナンスのお知らせ</div>
        <div className="notice-content">
          <p>下記日時にてメンテナンスを実施させていただきます。</p>
          <p>期間中はCloomをご利用いただけません。</p>
          <p>{startTime}～{endTime}</p>
          <p>※時間は前後する場合がございます。</p>
        </div>
      </div>
    );
  };

  signIn = async (event) => {
    if (event) event.preventDefault();

    const { login, password } = this.state.form;
    if (!login || !password) return;

    this.setState({ isLoading: true });

    try {
      const user = await authService.signIn(login, password);
      if (user.forceChangePW) {
        FpSdk.modules.store.dispatch(MemoryActions.set('user', user));
      } else {
        this.afterLoginSuccess(user);
      }
    } catch (err) {
      // Add new isLoading
      Toaster.error(err.message);
    }

    this.setState({ isLoading: false });
  }

  handleCompleteNewPassword = async (newPassword) => {
    const user = await authService.completeNewPasswordChallenge(newPassword);
    this.afterLoginSuccess(user);
  }

  handleCancelNewPassword = async () => {
    signOut(false).catch(err => {
      console.error(err);
    }).finally(() => {
      this.setState({
        newPasswordRequired: false,
      });
    });
  }

  renderNewPasswordRequired = () => {
    return (
      <FpNewPasswordRequired
        color={this.state.color}
        onCompletePassword={this.handleCompleteNewPassword}
        onCancel={this.handleCancelNewPassword}
        brand={this.props.brand}
      />
    );
  }

  handleInputChange(event) {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;
    var form = this.state.form;
    form[name] = value;
    this.setState({
      form: form,
    });
  }

  handleIdpError(errorMessage) {
    this.setState({ error: errorMessage });
  }

  async afterLoginSuccess(user) {
    // Validate authentication method before proceeding
    const isValid = await validateAuthMethod();
    if (!isValid) {
      // Sign out the user
      await signOut();

      // Show error alert
      showAuthMethodError();
      return;
    }

    // Validation passed, proceed with normal login flow
    FpSdk.modules.store.dispatch(MemoryActions.set('user', user));
    Toaster.success('AuthenticationLoginSuccess');
  }

  renderLoginForm() {
    // Use brand prop to determine which logo to display
    // Requirements: 4_3
    const brand = this.props.brand || 'cloom';
    const logoSrc = brand === 'roomai' ? '/assets/img/roomai-logo.png' : '/assets/img/cloom-logo.png';
    const logoAlt = brand === 'roomai' ? 'RoomAI Logo' : 'Cloom Logo';

    return (
      <div className={`new-login-container ${brand === 'roomai' ? 'brand-roomai' : 'brand-cloom'}`} >
        {
          this.state.error && (
            <div className="error-message">
              {this.state.error}
            </div>
          )
        }

        {/* Left Panel - Logo and Maintenance Notice */}
        <div className="login-left-panel" >
          <div className="logo-section">
            <div className="cloom-logo">
              <img src={logoSrc} alt={logoAlt} className="logo-image" />
            </div>
            {process.env.REACT_APP_ENV_TYPE !== 'prod' && (
              <div className="environment-text">{'開発環境'}</div>
            )}
          </div>

          {this.renderMaintenanceNotice()
          }

          < div className="version-info" > Version: {process.env.REACT_APP_VERSION}</div >
        </div >

        {/* Right Panel - Login Form */}
        < div className="login-right-panel" >
          <div className="login-form-container">
            <h1 className="login-title">ログイン</h1>

            <form className="login-form" onSubmit={this.signIn}>
              <div className="form-group">
                <label className="form-label">ユーザー名</label>
                <input
                  className="form-input"
                  placeholder=""
                  type="text"
                  value={this.state.form.login}
                  onChange={this.handleInputChange}
                  name="login"
                />
              </div>

              <div className="form-group">
                <label className="form-label">パスワード</label>
                <input
                  className="form-input"
                  placeholder=""
                  type="password"
                  value={this.state.form.password}
                  onChange={this.handleInputChange}
                  name="password"
                />
              </div>

              <div className="forgot-password-container">
                <a
                  onClick={() => this.setState({ forgotPassword: true })}
                  className="forgot-password-link"
                >
                  パスワードを忘れた場合はこちら
                </a>
              </div>

              <button
                className="login-button"
                type="submit"
                disabled={this.state.isLoading}
              >
                ログイン
              </button>

              {this.state.return && (
                <button
                  className="return-button"
                  type="button"
                  onClick={this.props.close}
                >
                  {FpTranslate('Return')}
                </button>
              )}
            </form>
          </div>
        </div >
      </div >
    );
  }

  render() {
    if (this.state.forgotPassword) {
      return (
        <FpPasswordForgot
          close={() => this.setState({ forgotPassword: false })}
          color={this.state.color}
          brand={this.props.brand}
        />
      );
    }
    if (this.props.user?.forceChangePW) {
      return this.renderNewPasswordRequired();
    }
    if (!this.state.login.session) {
      return this.renderLoginForm();
    }

    return <FpLoader />;
  }
}

FpClientAuthorityManager.propTypes = {
  auth_mode: PropTypes.shape({
    _id: PropTypes.string,
  }),
  preferences: PropTypes.object,
  close: PropTypes.func,
  brand: PropTypes.string, // 'cloom' or 'roomai'
};

export default FpClientAuthorityManager;
